import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Chip, Popover, TextField } from "@outpave/ds";
import { focusAdjacent } from "utils/focus";

export type AmountRange = {
  min?: number;
  max?: number;
};

type AmountRangeChipProps = {
  label: string;
  currency?: string | null;
  value: AmountRange;
  onChange: (next: AmountRange) => void;
};

const EMPTY: AmountRange = {};

// Strip the browser's number-input spin buttons (the up/down arrows that
// appear on hover / focus). Applied to TextField's `sx`, which forwards to
// the inner input element.
const hideSpinnerSx = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
} as const;

const hasValue = (r: AmountRange) => r.min !== undefined || r.max !== undefined;

const formatMoney = (n: number, currency?: string | null) =>
  n.toLocaleString(
    undefined,
    currency ? { style: "currency", currency } : undefined,
  );

const summarize = (r: AmountRange, currency?: string | null): string => {
  if (r.min !== undefined && r.max !== undefined) {
    return `${formatMoney(r.min, currency)} – ${formatMoney(r.max, currency)}`;
  }
  if (r.min !== undefined) return `≥ ${formatMoney(r.min, currency)}`;
  if (r.max !== undefined) return `≤ ${formatMoney(r.max, currency)}`;
  return "";
};

// Parse the user's draft input. Empty strings clear the side; anything that
// doesn't parse to a finite number is treated as "unset" so the popover
// never commits NaN.
const parseDraft = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

const AmountRangeChip: React.FC<AmountRangeChipProps> = ({
  label,
  currency = "USD",
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [minDraft, setMinDraft] = useState("");
  const [maxDraft, setMaxDraft] = useState("");
  const chipRef = useRef<HTMLElement | null>(null);

  const selected = hasValue(value);
  const chipLabel = selected
    ? `${label} · ${summarize(value, currency)}`
    : label;

  // Each opening starts from the committed range, so a draft nobody applied does not survive a close.
  const resetDrafts = () => {
    setMinDraft(value.min !== undefined ? String(value.min) : "");
    setMaxDraft(value.max !== undefined ? String(value.max) : "");
  };

  return (
    <Popover
      panelSx={{ width: "auto", minWidth: 320, padding: "var(--space-5)" }}
      trigger={({ open, toggle }) => (
        <Chip
          label={chipLabel}
          trailing="chevron"
          onClick={(e) => {
            chipRef.current = e.currentTarget;
            if (!open) resetDrafts();
            toggle();
          }}
          onClear={selected ? () => onChange(EMPTY) : undefined}
          // Raw Chip, so it does not name itself the way SelectFilter does.
          aria-label={label}
        />
      )}
    >
      {(close) => (
        <Stack
          // Tabbing past either end of the popover closes it and moves focus outside.
          onKeyDown={(e) => {
            if (e.key !== "Tab") return;
            const focusables = Array.from(
              e.currentTarget.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
              ),
            );
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement as HTMLElement | null;
            const leavingForward = !e.shiftKey && active === last;
            const leavingBackward = e.shiftKey && active === first;
            if (!leavingForward && !leavingBackward) return;
            e.preventDefault();
            close();
            requestAnimationFrame(() =>
              focusAdjacent(
                chipRef.current,
                e.shiftKey ? "backward" : "forward",
              ),
            );
          }}
          sx={{
            gap: "var(--space-5)",
          }}
        >
          <Stack
            direction="row"
            sx={{
              gap: "var(--space-4)",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label={t("Min")}
                leading={
                  currency ? (
                    <Typography component="span">
                      {
                        new Intl.NumberFormat(undefined, {
                          style: "currency",
                          currency,
                        })
                          .formatToParts(0)
                          .find((part) => part.type === "currency")?.value
                      }
                    </Typography>
                  ) : undefined
                }
                type="number"
                inputMode="decimal"
                placeholder="0"
                value={minDraft}
                onChange={(e) => setMinDraft(e.target.value)}
                fullWidth
                sx={hideSpinnerSx}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label={t("Max")}
                leading={
                  currency ? (
                    <Typography component="span">
                      {
                        new Intl.NumberFormat(undefined, {
                          style: "currency",
                          currency,
                        })
                          .formatToParts(0)
                          .find((part) => part.type === "currency")?.value
                      }
                    </Typography>
                  ) : undefined
                }
                type="number"
                inputMode="decimal"
                placeholder={t("No max")}
                value={maxDraft}
                onChange={(e) => setMaxDraft(e.target.value)}
                fullWidth
                sx={hideSpinnerSx}
              />
            </Box>
          </Stack>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "var(--space-3)",
            }}
          >
            <Button
              variant="primary-soft"
              onClick={() => {
                onChange(EMPTY);
                close();
              }}
              disabled={!selected}
            >
              {t("Clear")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={() => {
                onChange({
                  min: parseDraft(minDraft),
                  max: parseDraft(maxDraft),
                });
                close();
              }}
            >
              {t("Apply")}
            </Button>
          </Box>
        </Stack>
      )}
    </Popover>
  );
};

export default AmountRangeChip;
